import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Trash2, Loader2, X, AlertTriangle, Archive } from 'lucide-react';

const VARIANT_CONFIG = {
  trash:   { Icon: Trash2,        iconBg: 'bg-amber-50',  iconColor: 'text-amber-500', btnClass: 'btn-danger' },
  purge:   { Icon: AlertTriangle, iconBg: 'bg-red-100',   iconColor: 'text-red-500',   btnClass: 'btn-danger' },
  archive: { Icon: Archive,       iconBg: 'bg-blue-100',  iconColor: 'text-blue-500',  btnClass: 'btn-primary' },
};

/**
 * Confirmation d'un geste destructif — la pop-in commune à tous les écrans
 * (wiki, projets, tâches, corbeilles, builder).
 *
 * Trois réglages OPTIONNELS, ajoutés pour le builder et sans effet sur les
 * appelants qui ne les passent pas :
 *  - `error` : le refus s'affiche DANS la pop-in, qui reste ouverte. Sans lui,
 *    un échec ne se voyait nulle part (les appelants historiques le journalisent
 *    en console) — inacceptable pour un geste qu'on vient de confirmer.
 *  - `children` : un avertissement propre au contexte, sous la description.
 *  - `elevated` : rendu en PORTAIL sur <body> au z-index des surfaces qui
 *    passent devant tout le chrome. Sans lui, la pop-in reste au z-50 en place —
 *    ce que transperce le canevas du builder (un bloc peut déclarer `z-[100]`,
 *    le panneau Assistant est en `z-[200]`).
 */
export default function ConfirmTrashModal({
  isOpen,
  onConfirm,
  onCancel,
  title = 'Déplacer dans la corbeille',
  description = '',
  confirmLabel = 'Supprimer',
  loading = false,
  variant = 'trash',
  error = '',
  elevated = false,
  children = null,
}) {
  const cancelRef = useRef(null);
  const titleId = useId();

  // Rendre le focus à ce qui l'avait — mais seulement si l'élément est TOUJOURS
  // dans le document : un geste qui démonte son propre déclencheur (supprimer
  // depuis la barre du builder, puis quitter l'écran) n'a plus rien à refocaliser.
  //
  // DÉCLARÉ AVANT l'effet qui donne le focus à « Annuler » : React joue les
  // effets dans l'ordre de déclaration, et l'inverse mémorisait le bouton
  // « Annuler » de la pop-in elle-même — donc plus rien à rendre à la fermeture.
  const openerRef = useRef(null);
  useEffect(() => {
    if (!isOpen) return undefined;
    openerRef.current = document.activeElement;
    return () => {
      const opener = openerRef.current;
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, [isOpen]);

  // Piège de focus (WCAG 2.4.3, motif « dialog ») : tant que la boîte est
  // ouverte, Tab boucle sur SON contenu. Longtemps inutile — deux boutons et
  // une croix, on en sortait sans conséquence —, c'est devenu nécessaire depuis
  // qu'un appelant y met un formulaire (`children`) : au Tab on partait dans la
  // page derrière, pourtant annoncée inerte par `aria-modal="true"`.
  const boxRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape' && !loading) { onCancel(); return; }
      if (e.key !== 'Tab') return;
      const box = boxRef.current;
      if (!box) return;
      const focusable = [...box.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )].filter((el) => el.getClientRects().length > 0);
      if (focusable.length === 0) return;
      // Un groupe de boutons radio ne prend qu'UN arrêt de tabulation : celui
      // qui est coché (ou le premier si aucun ne l'est). Sans ce repli, la
      // boucle rendrait le focus à une option non retenue — que le navigateur
      // sélectionnerait au passage, changeant le choix de l'utilisateur.
      const stops = focusable.filter((el) => {
        if (el.type !== 'radio' || !el.name) return true;
        const group = focusable.filter((o) => o.type === 'radio' && o.name === el.name);
        return el === (group.find((o) => o.checked) || group[0]);
      });
      const first = stops[0];
      const last = stops[stops.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handler);
    cancelRef.current?.focus();
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, loading, onCancel]);

  if (!isOpen) return null;

  const cfg = VARIANT_CONFIG[variant] || VARIANT_CONFIG.trash;
  const { Icon, iconBg, iconColor, btnClass } = cfg;

  const modal = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={elevated ? { zIndex: 'var(--z-modal-over)' } : undefined}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={loading ? undefined : onCancel} />
      <div ref={boxRef} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md border border-stone-200 overflow-hidden">
        <div className="px-6 pt-6 pb-4 flex flex-col items-center text-center">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${iconBg}`}>
            <Icon size={22} className={iconColor} />
          </div>
          <h3 id={titleId} className="text-base font-semibold text-stone-900 mb-1">{title}</h3>
          {description && (
            <p className="text-sm text-stone-500 leading-relaxed">{description}</p>
          )}
          {children}
          {error && (
            <p role="alert" className="alert alert-danger mt-4 text-left">{error}</p>
          )}
        </div>

        <div className="px-6 pb-5 flex items-center justify-center gap-3">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="btn btn-secondary"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`btn ${btnClass}`}
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            {confirmLabel}
          </button>
        </div>

        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="absolute top-3 right-3 p-1.5 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors disabled:opacity-50"
          aria-label="Fermer"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );

  return elevated ? createPortal(modal, document.body) : modal;
}
